import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Divider,
  FormHelperText,
  Grid2,
  LinearProgress,
  TextField,
  Typography
} from "@mui/material";
import type {
  AutocompleteChangeDetails,
  AutocompleteChangeReason
} from "@mui/material/Autocomplete";
import type { ChangeEvent, SyntheticEvent } from "react";
import { ExpandMore } from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { UserOrEmailMapping } from "../../Admin/typings/admin.types";
import { useDispatch, useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { BookingInputs } from "../../../components/FacilityManager/Domain/Types/BookingInputs.type";
import { useIsUserServiceAdminForLocation } from "../../../hooks/Remote/Admin/useIsUserServiceAdminForLocation";
import { useRemoteMutateFetchFloorPlanService } from "../../../hooks/Remote/FloorPlan/useRemoteMutateFetchFloorPlanService";
import { useRemoteFetchBookableEquipments } from "../../../hooks/Remote/BookableEquipment/useRemoteFetchBookableEquipment";
import { useRemoteFetchMaxSetupTime } from "../../../hooks/Remote/Services/BookingServices/useRemoteFetchMaxSetupTime";
import { useRemoteFetchAllCateringServices } from "../../../hooks/Remote/Services/CateringMenu/useRemoteFetchAllCateringServices";
import { useRemoteFetchRoomlayout } from "../../../hooks/Remote/Services/Roomlayout/useRemoteFetchRoomlayout";
import { useRemoteFetchPermissionContext } from "../../../hooks/Remote/User/UserInfo/useRemoteFetchPermissionContext";
import { setInputs } from "../../Booking-Form/slices/booking.slice";
import { BookingType } from "../../Booking-Form/typings/booking-inputs";
import { IFloorPayload } from "../../../components/FacilityManager/Domain/Types/FloorPlan/FloorPayload.type";
import { BookingServicePromptDialogContents } from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/BookingServicePromptDialogContents";
import { BookingServiceMapConfig } from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/BookingServicePromptDialog";
import {
  BookingServiceEditedData,
  ServiceOrderDto
} from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/Domain/BookingServicePrompt.types";
import {
  handleAssignedBookableEquipItems,
  handleAssignedCateringItems,
  handleAssignedRoomLayItems
} from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/functions/handleAssignedServiceItems.function";
import { mapBookEquipmentTableData } from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/BookableEquipment/BookingService-BookableEquipment.functions";
import { mapCateringEditedData } from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/Catering/BookingService-CateringTable.functions";
import { mapRoomLayoutTableData } from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/RoomLayout/BookingService-RoomLayoutTable.functions";
import { useRemoteFetchUserInfoForAllUsers } from "../../../hooks/Remote/User/UserInfo/useRemoteFetchUserInfoForAllUsers";
import { getOptionLabelOfUserOrEmailMapping } from "../../../utils/type/type.utils";
import { InfoIconWithTooltip } from "../../../components/Title/InfoIcon";
import { computeSelectionPrompt2Derived } from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/SelectionPrompt/selectionPrompt2Derived";
import MsTeamsCallSwitch from "../../../components/BookingForm/msTeams-call-switch.component";
import {
  filterParticipantsOpt,
  handleClearRequiredOpt,
  handleSelectOptionalOpt,
  handleSelectOrRemoveRequiredOpt,
  isOptionEqualToVal,
  mergeRequiredAndOptionalUsers,
  renderParticiapantTags
} from "../../../components/BookingForm/functions/form-zone-restriction.functions";
import { getPlanDateRangeOrNull } from "../../Booking-Form/functions/floor.functions";
import { BookableBookingObjectDto } from "../typings/resource-calendar.types";
import {
  SCHEDULE_RESOURCE_BOOK_SECTION_RADIUS_PX,
  scheduleResourceBookAccordionShellNeutralSx,
  scheduleResourceBookAccordionShellPrimarySx,
  scheduleResourceBookAccordionSummaryNeutralSx,
  scheduleResourceBookAccordionSummaryPrimarySx
} from "./scheduleResourceBookSectionSurfaces";
import { isMandatoryConferenceServiceIncomplete } from "../../../components/FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/functions/hasMandatory.function";

export function usersExcludingRequired(
  candidates: readonly UserOrEmailMapping[],
  requiredParticipants: readonly UserOrEmailMapping[]
): UserOrEmailMapping[] {
  const requiredUserIds = new Set(requiredParticipants.map(p => p.userId));
  return candidates.filter(u => !requiredUserIds.has(u.userId));
}

type P = {
  row: BookableBookingObjectDto;
  bookingFrom: string;
  bookingTo: string;
  chosenTimes: { endTime: string; startTime: string };
  /** Resource book dialog `open`; used to reset local service UI when Redux was cleared on close. */
  dialogOpen: boolean;
  /** When false, resource-book dialog disables Next (same incomplete rules as normal conference booking). */
  onConferenceInviteStepValidityChange?: (canProceed: boolean) => void;
  /** True while the dialog keeps this step mounted off-screen to load services before showing step 1. */
  prefetchMode?: boolean;
  /** Fired once when floor plan + max setup (and related catalog data) are ready during prefetch. */
  onConferenceServicesPrefetchReady?: () => void;
};

const emptyEditedService: BookingServiceEditedData = {
  cateringData: [],
  bookableEquipmentData: [],
  roomLayoutData: []
};

const emptyOrderedService: ServiceOrderDto = {
  caterings: [],
  costCenterId: null,
  bookableEquipments: [],
  roomLayout: []
};

export function ScheduleResourceBookConferenceStep({
  row,
  bookingFrom,
  bookingTo,
  chosenTimes,
  dialogOpen,
  onConferenceInviteStepValidityChange,
  prefetchMode = false,
  onConferenceServicesPrefetchReady
}: Readonly<P>) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { inputs } = useSelector((state: RootState) => state.booking);
  const {
    userInformation: {
      sub,
      msGraphEnabled,
      company: {
        meta: { calendarSyncPossible, activateBookingServices }
      }
    },
    userRoles: { isRequestAdmin }
  } = useSelector((state: RootState) => state.login);

  const [users, setUsers] = useState<UserOrEmailMapping[]>([]);
  const [requiredUsers, setRequiredUsers] = useState<UserOrEmailMapping[]>([]);
  const [optionalUsers, setOptionalUsers] = useState<UserOrEmailMapping[]>([]);
  const [optionalUsersOptions, setOptionalUsersOptions] = useState<UserOrEmailMapping[]>([]);
  const [showOptionalParticipants, setShowOptionalParticipants] = useState(false);
  const [basicInfoExpanded, setBasicInfoExpanded] = useState(true);
  const [servicesExpanded, setServicesExpanded] = useState(false);
  const [floorPlanForService, setFloorPlanForService] = useState<IFloorPayload | undefined>(
    undefined
  );
  const [serviceDataLoading, setServiceDataLoading] = useState(false);
  const [serviceFloorPlanRequestFinished, setServiceFloorPlanRequestFinished] = useState(false);
  const [editedService, setEditedService] = useState<BookingServiceEditedData>(emptyEditedService);
  const [orderedService, setOrderedService] = useState<ServiceOrderDto>(emptyOrderedService);
  const [selectTab, setSelectTab] = useState(0);

  const hydratedZoneAccessFromInputsRef = useRef(false);
  const persistedServiceRef = useRef("");
  const serviceInitKeyRef = useRef("");
  /** Same as BookingServicePromptDialog: pick first mandatory tab once per service init. */
  const servicesTabsInitializedRef = useRef(false);
  const prefetchReadyEmittedRef = useRef(false);

  const { data: remoteUsers, isFetching: isRemoteUsersFetching } =
    useRemoteFetchUserInfoForAllUsers();
  const { data: remoteCateringMenu } = useRemoteFetchAllCateringServices(row.locationInventoryId);
  const { data: remoteBookableEquipment } = useRemoteFetchBookableEquipments();
  const { data: remoteRoomLayout } = useRemoteFetchRoomlayout();
  const { data: permissionContextData } = useRemoteFetchPermissionContext(true);
  const permissionContext = permissionContextData?.[0];

  const { mutateAsync: fetchFloorPlanForService } = useRemoteMutateFetchFloorPlanService();
  const { data: isServiceAdminForLocation, isServiceAdminLookupPending } =
    useIsUserServiceAdminForLocation(row.locationInventoryId ?? 0, sub, isRequestAdmin);
  const isServiceAdminBypass = isServiceAdminForLocation === true;

  useEffect(() => {
    if (!dialogOpen) return;
    hydratedZoneAccessFromInputsRef.current = false;
    persistedServiceRef.current = "";
    serviceInitKeyRef.current = "";
    servicesTabsInitializedRef.current = false;
    prefetchReadyEmittedRef.current = false;
    setEditedService(emptyEditedService);
    setOrderedService(emptyOrderedService);
  }, [dialogOpen, row.bookingInventoryId]);

  useEffect(() => {
    if (dialogOpen) return;
    setServiceFloorPlanRequestFinished(false);
  }, [dialogOpen]);

  const serviceFilterTimezone = inputs.timezone ?? "UTC";
  const { data: remoteMaxSetupTime } = useRemoteFetchMaxSetupTime(
    {
      bookingInventoryIds: [row.bookingInventoryId],
      startDate: bookingFrom,
      endDate: bookingTo,
      startTime: chosenTimes.startTime,
      endTime: chosenTimes.endTime,
      bookingType: BookingType.CONFERENCEZONE,
      timezone: serviceFilterTimezone,
      weekdays: inputs.weekdays,
      monday: "no use"
    },
    row.bookingType === BookingType.CONFERENCEZONE && bookingFrom !== "" && bookingTo !== ""
  );

  const selectedZoneScheduleId = useMemo(() => {
    return floorPlanForService?.zones.find(zone => zone.inventory?.id === row.bookingInventoryId)
      ?.id;
  }, [floorPlanForService, row.bookingInventoryId]);

  const bookingInputsForService = useMemo(
    () =>
      ({
        ...inputs,
        bookingFrom,
        bookingTo,
        bookingStart: chosenTimes.startTime,
        bookingEnd: chosenTimes.endTime
      }) as BookingInputs,
    [bookingFrom, bookingTo, chosenTimes.endTime, chosenTimes.startTime, inputs]
  );

  const conferenceServiceFromStore = inputs.conferenceService?.[row.bookingInventoryId];

  const serviceState = useMemo(() => {
    if (selectedZoneScheduleId == null) {
      return {
        hasMandatory: false,
        hasOrderableService: false,
        isConferenceServicesActionEnabled: false
      };
    }
    const selectedZoneEntry = floorPlanForService?.zones.find(
      zone => zone.id === selectedZoneScheduleId
    );

    if (!selectedZoneEntry) {
      return {
        hasMandatory: false,
        hasOrderableService: false,
        isConferenceServicesActionEnabled: false
      };
    }

    return computeSelectionPrompt2Derived({
      bookingInputs: bookingInputsForService,
      floorPlanForService,
      selectedZone: selectedZoneScheduleId,
      bookingFrom,
      bookingStart: chosenTimes.startTime,
      maxSetupTime: remoteMaxSetupTime?.maxSetUpTime ?? 0,
      conferenceService: inputs.conferenceService,
      selectedEntry: selectedZoneEntry,
      selectableByVariant: true,
      isServiceAdminBypass
    });
  }, [
    bookingFrom,
    bookingInputsForService,
    chosenTimes.startTime,
    floorPlanForService,
    inputs.conferenceService,
    isServiceAdminBypass,
    remoteMaxSetupTime?.maxSetUpTime,
    row.bookingInventoryId,
    selectedZoneScheduleId
  ]);

  const servicesSectionLoading = useMemo(() => {
    if (row.bookingType !== BookingType.CONFERENCEZONE) return false;
    if (!activateBookingServices) return false;

    if (serviceDataLoading) return true;
    if (selectedZoneScheduleId == null) return false;

    return (
      !remoteMaxSetupTime ||
      !remoteCateringMenu ||
      !remoteBookableEquipment ||
      !remoteRoomLayout ||
      !permissionContextData ||
      isServiceAdminLookupPending
    );
  }, [
    activateBookingServices,
    isServiceAdminLookupPending,
    permissionContextData,
    remoteBookableEquipment,
    remoteCateringMenu,
    remoteMaxSetupTime,
    remoteRoomLayout,
    row.bookingType,
    selectedZoneScheduleId,
    serviceDataLoading
  ]);

  /** Used so the dialog can advance only after floor plan + (when applicable) catalog/max-setup data are ready. */
  const servicesBackendPrefetchReady = useMemo(() => {
    if (!activateBookingServices) return true;
    if (row.bookingType !== BookingType.CONFERENCEZONE) return true;
    if (!row.floorInventoryId) return true;
    if (!serviceFloorPlanRequestFinished) return false;
    if (serviceDataLoading) return false;
    if (selectedZoneScheduleId == null) return true;
    return !!(
      remoteMaxSetupTime &&
      remoteCateringMenu &&
      remoteBookableEquipment &&
      remoteRoomLayout &&
      permissionContextData &&
      !isServiceAdminLookupPending
    );
  }, [
    activateBookingServices,
    isServiceAdminLookupPending,
    permissionContextData,
    remoteBookableEquipment,
    remoteCateringMenu,
    remoteMaxSetupTime,
    remoteRoomLayout,
    row.bookingType,
    row.floorInventoryId,
    selectedZoneScheduleId,
    serviceDataLoading,
    serviceFloorPlanRequestFinished
  ]);

  useEffect(() => {
    if (!prefetchMode) {
      prefetchReadyEmittedRef.current = false;
      return;
    }
    if (!servicesBackendPrefetchReady) return;
    if (prefetchReadyEmittedRef.current) return;
    prefetchReadyEmittedRef.current = true;
    onConferenceServicesPrefetchReady?.();
  }, [onConferenceServicesPrefetchReady, prefetchMode, servicesBackendPrefetchReady]);

  const conferenceServiceMergedForValidation = useMemo(
    () => ({
      ...inputs.conferenceService,
      [row.bookingInventoryId]: orderedService
    }),
    [inputs.conferenceService, orderedService, row.bookingInventoryId]
  );

  const hasIncompleteMandatoryConferenceService = useMemo(() => {
    if (row.bookingType !== BookingType.CONFERENCEZONE) return false;
    if (!activateBookingServices) return false;
    if (servicesSectionLoading) return false;

    return isMandatoryConferenceServiceIncomplete({
      isConferenceZoneBooking: true,
      floorPlanForService,
      selectedZone: selectedZoneScheduleId,
      conferenceServiceInventoryKey: row.bookingInventoryId,
      conferenceService: conferenceServiceMergedForValidation
    });
  }, [
    activateBookingServices,
    conferenceServiceMergedForValidation,
    floorPlanForService,
    row.bookingInventoryId,
    row.bookingType,
    selectedZoneScheduleId,
    servicesSectionLoading
  ]);

  const conferenceInviteStepCanProceed = useMemo(() => {
    // bypass booking type check for non-conference zone bookings
    if (row.bookingType !== BookingType.CONFERENCEZONE) return true;

    // check if subject is present
    if (!(inputs.subject ?? "").trim()) return false;

    // check if service booking is activated. to proceed service booking checking logic below
    if (!activateBookingServices) return true;

    if (servicesSectionLoading) return false;

    // check if mandatory services are fulfilled
    return !hasIncompleteMandatoryConferenceService;
  }, [
    activateBookingServices,
    hasIncompleteMandatoryConferenceService,
    inputs.subject,
    row.bookingType,
    servicesSectionLoading
  ]);

  useLayoutEffect(() => {
    onConferenceInviteStepValidityChange?.(conferenceInviteStepCanProceed);
  }, [conferenceInviteStepCanProceed, onConferenceInviteStepValidityChange]);

  /** Same gate as selectionPrompt2Derived: `isConferenceServicesActionEnabled` already means orderable now or existing order (includes `hasOrderableService`). */
  const servicesAccordionExpandable = useMemo(() => {
    return !servicesSectionLoading && serviceState.isConferenceServicesActionEnabled;
  }, [serviceState.isConferenceServicesActionEnabled, servicesSectionLoading]);

  const mappedTabs: BookingServiceMapConfig[] = useMemo(() => {
    const tabConfigs: BookingServiceMapConfig[] = [
      {
        key: "catering",
        label: "Catering",
        editedData: editedService.cateringData,
        usedItems: orderedService.caterings.filter(c => c.qty > 0).length,
        isMandatory: editedService.cateringData.some(item => item.selectingIsMandatory === true),
        testId: "schedule-resource-book-services-tab-catering",
        originalIndex: 0
      },
      {
        key: "equipment",
        label: "Equipment",
        editedData: editedService.bookableEquipmentData,
        usedItems: orderedService.bookableEquipments.length,
        isMandatory: editedService.bookableEquipmentData.some(
          item => item.selectingIsMandatory === true
        ),
        testId: "schedule-resource-book-services-tab-equipment",
        originalIndex: 1
      },
      {
        key: "roomLayout",
        label: "Room Layout",
        editedData: editedService.roomLayoutData,
        usedItems: orderedService.roomLayout.length,
        isMandatory: editedService.roomLayoutData.some(item => item.selectingIsMandatory === true),
        testId: "schedule-resource-book-services-tab-layout",
        originalIndex: 2
      }
    ];

    const visibleTabs = tabConfigs.filter(tab => tab.editedData.length > 0 || tab.usedItems > 0);
    return visibleTabs.map((tab, index) => ({ ...tab, mappedIndex: index }));
  }, [editedService, orderedService]);

  const hasMandatoryConferenceService = mappedTabs.some(tab => tab.isMandatory);

  const handleToggleOptionalParticipants = () => {
    const nextShowOptional = !showOptionalParticipants;
    setShowOptionalParticipants(nextShowOptional);
    if (nextShowOptional) {
      setOptionalUsersOptions(usersExcludingRequired(users, requiredUsers));
    }
  };

  const handleRequiredParticipantsChange = useCallback(
    (
      _event: SyntheticEvent,
      newValue: (string | UserOrEmailMapping)[] | null,
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<UserOrEmailMapping | string>
    ) => {
      const nextRequiredSelection = (newValue ?? []) as UserOrEmailMapping[];
      const participantFromDetails = details?.option as UserOrEmailMapping;

      switch (reason) {
        case "selectOption":
        case "removeOption": {
          handleSelectOrRemoveRequiredOpt({
            state: {
              valOpt: nextRequiredSelection,
              reason,
              details,
              optionalUsers,
              selectedOpt: participantFromDetails
            },
            action: {
              setRequiredUsers,
              setOptionalUsers,
              dispatch,
              setInputs,
              setOptionalUsersOptions,
              enqueueSnackbar
            }
          });
          return;
        }
        case "clear": {
          handleClearRequiredOpt({
            state: {
              valOpt: nextRequiredSelection,
              optionalUsers,
              requiredUsers
            },
            action: { setRequiredUsers, dispatch, setInputs, setOptionalUsersOptions }
          });
          return;
        }
        default: {
          return;
        }
      }
    },
    [dispatch, enqueueSnackbar, optionalUsers, requiredUsers]
  );

  const handleOptionalParticipantsChange = useCallback(
    (
      _event: SyntheticEvent,
      newValue: (string | UserOrEmailMapping)[] | null,
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<UserOrEmailMapping | string>
    ) => {
      const nextOptionalSelection = (newValue ?? []) as UserOrEmailMapping[];

      switch (reason) {
        case "selectOption":
        case "removeOption": {
          handleSelectOptionalOpt({
            state: { reason, details, optionalVal: nextOptionalSelection, requiredUsers },
            action: { setOptionalUsers, dispatch, setInputs, enqueueSnackbar }
          });
          return;
        }
        case "clear": {
          setOptionalUsers(nextOptionalSelection);
          const updatedZoneAccess = mergeRequiredAndOptionalUsers(
            requiredUsers,
            nextOptionalSelection
          );
          dispatch(setInputs({ zoneAccess: updatedZoneAccess }));
          return;
        }
        default: {
          return;
        }
      }
    },
    [dispatch, enqueueSnackbar, requiredUsers, setInputs, setOptionalUsers]
  );

  useEffect(() => {
    if (!remoteUsers || isRemoteUsersFetching) return;
    if (users.length > 0) return;
    setUsers(remoteUsers.filter(user => user.userId !== sub) as UserOrEmailMapping[]);
  }, [isRemoteUsersFetching, remoteUsers, sub, users.length]);

  useEffect(() => {
    if (inputs.zoneAccess && inputs.zoneAccess.length > 0) {
      setOptionalUsersOptions(usersExcludingRequired(optionalUsersOptions, requiredUsers));
    }
  }, [inputs.zoneAccess, requiredUsers, optionalUsers]);

  useEffect(() => {
    if (!dialogOpen) return;
    if (row.bookingType !== BookingType.CONFERENCEZONE) return;
    if (!row.floorInventoryId) {
      setFloorPlanForService(undefined);
      setServiceFloorPlanRequestFinished(true);
      return;
    }
    const planRange = getPlanDateRangeOrNull(
      bookingFrom,
      inputs.bookingStart ?? null,
      bookingTo,
      inputs.bookingEnd ?? null
    );
    if (!planRange) {
      setFloorPlanForService(undefined);
      setServiceFloorPlanRequestFinished(true);
      return;
    }

    let cancelled = false;
    setServiceFloorPlanRequestFinished(false);
    setServiceDataLoading(true);

    fetchFloorPlanForService({
      floorInventoryId: row.floorInventoryId,
      dto: { end: planRange.end, start: planRange.start }
    })
      .then(data => {
        if (!cancelled) {
          setFloorPlanForService(data);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFloorPlanForService(undefined);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setServiceDataLoading(false);
          setServiceFloorPlanRequestFinished(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    bookingFrom,
    bookingTo,
    dialogOpen,
    fetchFloorPlanForService,
    inputs.bookingEnd,
    inputs.bookingStart,
    row.bookingType,
    row.floorInventoryId
  ]);

  useEffect(() => {
    const selectedZone = selectedZoneScheduleId;
    if (selectedZone == null) return;
    /** istanbul ignore next */
    if (!floorPlanForService || !remoteMaxSetupTime || isServiceAdminLookupPending) return;
    /** istanbul ignore next */
    if (!remoteCateringMenu || !remoteBookableEquipment || !remoteRoomLayout || !permissionContext)
      return;

    const initKey = [
      row.bookingInventoryId,
      selectedZone,
      bookingFrom,
      bookingTo,
      chosenTimes.startTime,
      chosenTimes.endTime
    ].join("|");
    if (serviceInitKeyRef.current === initKey) return;
    serviceInitKeyRef.current = initKey;
    servicesTabsInitializedRef.current = false;

    const assignedCateringItems = handleAssignedCateringItems({
      floorPlanForService,
      selectedZone,
      remoteCateringMenu,
      permissionContext,
      userId: sub
    });
    const assignedBookableEquipItems = handleAssignedBookableEquipItems({
      floorPlanForService,
      selectedZone,
      remoteBookableEquipment
    });
    const assignedRoomLayoutItems = handleAssignedRoomLayItems({
      floorPlanForService,
      selectedZone,
      remoteRoomLayout: remoteRoomLayout.map(item => ({ ...item, arrangement: item.arrangement }))
    });

    setEditedService({
      cateringData: mapCateringEditedData(
        bookingInputsForService,
        assignedCateringItems,
        conferenceServiceFromStore,
        remoteMaxSetupTime.maxSetUpTime,
        isServiceAdminBypass,
        true
      ),
      bookableEquipmentData: mapBookEquipmentTableData(
        bookingInputsForService,
        assignedBookableEquipItems as any[],
        remoteMaxSetupTime.maxSetUpTime,
        isServiceAdminBypass
      ),
      roomLayoutData: mapRoomLayoutTableData(
        bookingInputsForService,
        assignedRoomLayoutItems as any[],
        remoteMaxSetupTime.maxSetUpTime,
        isServiceAdminBypass
      )
    });

    setOrderedService({
      caterings: conferenceServiceFromStore?.caterings ?? [],
      costCenterId: conferenceServiceFromStore?.costCenterId ?? null,
      bookableEquipments: conferenceServiceFromStore?.bookableEquipments ?? [],
      roomLayout: conferenceServiceFromStore?.roomLayout ?? []
    });
  }, [
    bookingFrom,
    bookingInputsForService,
    bookingTo,
    chosenTimes.endTime,
    chosenTimes.startTime,
    conferenceServiceFromStore,
    floorPlanForService,
    isServiceAdminBypass,
    isServiceAdminLookupPending,
    permissionContext,
    remoteBookableEquipment,
    remoteCateringMenu,
    remoteMaxSetupTime,
    remoteRoomLayout,
    row.bookingInventoryId,
    selectedZoneScheduleId,
    sub
  ]);

  useEffect(() => {
    const selectedZone = selectedZoneScheduleId;
    if (selectedZone == null) return;

    const sanitizedOrder: ServiceOrderDto = {
      ...orderedService,
      caterings: orderedService.caterings.filter(item => item.qty > 0)
    };
    const persistKey = JSON.stringify({
      bookingInventoryId: row.bookingInventoryId,
      order: sanitizedOrder
    });
    if (persistedServiceRef.current === persistKey) return;
    persistedServiceRef.current = persistKey;

    dispatch(
      setInputs({
        conferenceService: {
          ...inputs.conferenceService,
          [row.bookingInventoryId]: sanitizedOrder
        }
      })
    );
  }, [
    dispatch,
    inputs.conferenceService,
    orderedService,
    row.bookingInventoryId,
    selectedZoneScheduleId
  ]);

  useEffect(() => {
    if (mappedTabs.length === 0) {
      setSelectTab(0);
      return;
    }
    if (!servicesTabsInitializedRef.current) {
      const firstMandatory = mappedTabs.find(tab => tab.isMandatory);
      setSelectTab(firstMandatory?.mappedIndex ?? mappedTabs[0].mappedIndex ?? 0);
      servicesTabsInitializedRef.current = true;
      return;
    }
    const hasCurrentTab = mappedTabs.some(tab => tab.mappedIndex === selectTab);
    if (!hasCurrentTab) {
      setSelectTab(mappedTabs[0].mappedIndex ?? 0);
    }
  }, [mappedTabs, selectTab]);

  useEffect(() => {
    if (hydratedZoneAccessFromInputsRef.current) return;
    if (users.length === 0) return;
    const za = inputs.zoneAccess;
    if (!za?.length) {
      hydratedZoneAccessFromInputsRef.current = true;
      return;
    }
    hydratedZoneAccessFromInputsRef.current = true;
    const required = za.filter(u => u.isRequired !== false);
    const optional = za.filter(u => u.isRequired === false);
    setRequiredUsers(required);
    setOptionalUsers(optional);
    if (optional.length > 0) {
      setShowOptionalParticipants(true);
    }
    setOptionalUsersOptions(usersExcludingRequired(users, required));
  }, [inputs.zoneAccess, users]);

  useEffect(() => {
    if (!servicesAccordionExpandable) {
      setServicesExpanded(false);
    }
  }, [servicesAccordionExpandable]);

  return (
    <Grid2
      container
      direction="column"
      spacing={prefetchMode ? 0 : 2}
      sx={{
        minWidth: 0,
        mt: prefetchMode ? 0 : 1,
        ...(prefetchMode
          ? {
              flexShrink: 0,
              maxHeight: 0,
              minHeight: 0,
              opacity: 0,
              overflow: "hidden",
              pointerEvents: "none"
            }
          : {})
      }}
    >
      <Accordion
        elevation={0}
        expanded={basicInfoExpanded}
        onChange={(_, expanded) => setBasicInfoExpanded(expanded)}
        sx={[scheduleResourceBookAccordionShellPrimarySx, { width: "100%" }]}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          data-testid="schedule-resource-book-basic-collapse"
          sx={scheduleResourceBookAccordionSummaryPrimarySx}
        >
          <Typography fontWeight={600} variant="subtitle1">
            {t("Invitation")}
          </Typography>
        </AccordionSummary>
        <Divider />
        <AccordionDetails
          sx={{
            background: "transparent",
            bgcolor: "transparent",
            pb: { sm: 2.5, xs: 2 },
            pt: 2,
            px: { sm: 2.5, xs: 2 }
          }}
        >
          <Grid2 container direction="column" rowGap={0}>
            <Grid2
              container
              alignItems={{ sm: "stretch", xs: "stretch" }}
              columnGap={2}
              rowGap={1.5}
              direction={{ sm: "row", xs: "column" }}
              justifyContent="start"
            >
              <Grid2 sx={{ width: { sm: "auto", xs: "100%" } }}>
                <TextField
                  data-testid="booking-conference-subject-textfield"
                  error={!inputs.subject}
                  id="schedule-resource-conference-subject"
                  label={t("Conference Subject")}
                  margin="dense"
                  onChange={(event: ChangeEvent<HTMLInputElement>) =>
                    dispatch(setInputs({ subject: event.target.value }))
                  }
                  placeholder={t("Add Subject")}
                  sx={{ marginBottom: 0, width: { sm: 300, xs: "100%" } }}
                  type="text"
                  value={inputs.subject || ""}
                  variant="outlined"
                />
              </Grid2>

              {msGraphEnabled && calendarSyncPossible && (
                <Grid2
                  data-testid="schedule-resource-book-conference-ms-teams-call-switch"
                  sx={{
                    display: "flex",
                    width: { sm: "auto", xs: "100%" }
                  }}
                >
                  <MsTeamsCallSwitch enableEdit />
                </Grid2>
              )}
            </Grid2>
            {!inputs.subject && (
              <FormHelperText error sx={{ ml: 1.75, mt: 0, width: { sm: 300, xs: "100%" } }}>
                {t("Subject is required")}
              </FormHelperText>
            )}
          </Grid2>

          <Grid2 alignItems="center" container direction="row" sx={{ pt: 2 }}>
            <Typography
              variant="h5"
              sx={theme => ({
                fontSize: {
                  xs: theme.typography.h6.fontSize,
                  sm: theme.typography.h5.fontSize
                }
              })}
            >
              {t("Who do you want to invite?")}
              <InfoIconWithTooltip tooltipText={t("_chosenColleagueNoAccessZone")} />
            </Typography>
          </Grid2>

          <Grid2
            alignItems="center"
            container
            direction={{ sm: "row", xs: "column" }}
            justifyContent="space-between"
            sx={{ display: "flex", marginTop: 1, rowGap: 1 }}
          >
            <Box
              sx={{
                alignSelf: { xs: "stretch", sm: "auto" },
                width: { xs: "100%", sm: "auto" },
                flex: { sm: "1 1 0%" },
                minWidth: { sm: 0 }
              }}
            >
              <Autocomplete
                disableCloseOnSelect
                filterOptions={filterParticipantsOpt}
                filterSelectedOptions
                freeSolo
                fullWidth
                getOptionLabel={getOptionLabelOfUserOrEmailMapping}
                handleHomeEndKeys
                id="schedule-resource-tags-required"
                isOptionEqualToValue={isOptionEqualToVal}
                multiple
                onChange={handleRequiredParticipantsChange}
                options={users}
                renderInput={params => (
                  <TextField
                    {...params}
                    fullWidth
                    placeholder={
                      showOptionalParticipants
                        ? t("Add required participants")
                        : t("Add participants or email")
                    }
                    variant="outlined"
                  />
                )}
                renderTags={renderParticiapantTags}
                value={requiredUsers}
              />
            </Box>
            <Button
              color="primary"
              data-testid="show-optional-btn"
              onClick={handleToggleOptionalParticipants}
              sx={[
                { alignSelf: { sm: "center", xs: "flex-end" }, marginLeft: { sm: 2, xs: 0 } },
                showOptionalParticipants ? { display: "none" } : { display: "block" }
              ]}
            >
              {t("Optional")}
            </Button>
          </Grid2>

          {showOptionalParticipants && (
            <Grid2
              alignItems="center"
              container
              direction={{ sm: "row", xs: "column" }}
              justifyContent="space-between"
              sx={{ mt: 0.5, rowGap: 1 }}
            >
              <Box
                sx={{
                  alignSelf: { xs: "stretch", sm: "auto" },
                  width: { xs: "100%", sm: "auto" },
                  flex: { sm: "1 1 0%" },
                  minWidth: { sm: 0 }
                }}
              >
                <Autocomplete
                  disableCloseOnSelect
                  filterOptions={filterParticipantsOpt}
                  filterSelectedOptions
                  freeSolo
                  fullWidth
                  getOptionLabel={getOptionLabelOfUserOrEmailMapping}
                  handleHomeEndKeys
                  id="schedule-resource-tags-optional"
                  isOptionEqualToValue={isOptionEqualToVal}
                  multiple
                  onChange={handleOptionalParticipantsChange}
                  options={optionalUsersOptions}
                  renderInput={params => (
                    <TextField
                      {...params}
                      fullWidth
                      placeholder={t("Add optional participants or email")}
                      variant="outlined"
                    />
                  )}
                  renderTags={renderParticiapantTags}
                  value={optionalUsers}
                />
              </Box>
            </Grid2>
          )}

          <Grid2
            alignItems="center"
            container
            data-testid="conferencezone-desc-field-container"
            direction="row"
            justifyContent="space-between"
            sx={{ marginTop: 2 }}
          >
            <TextField
              fullWidth
              id="schedule-resource-conference-description"
              label={t("Description")}
              margin="dense"
              multiline
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                dispatch(setInputs({ description: event.target.value }))
              }
              placeholder={t("Add a description and documents")}
              slotProps={{
                input: {
                  sx: {
                    alignItems: "flex-start",
                    "&::placeholder": {
                      textAlign: "left",
                      verticalAlign: "top"
                    }
                  }
                }
              }}
              type="text"
              value={inputs.description || ""}
              variant="outlined"
            />
          </Grid2>
        </AccordionDetails>
      </Accordion>

      <Box
        sx={{
          borderRadius: SCHEDULE_RESOURCE_BOOK_SECTION_RADIUS_PX,
          flexShrink: 0,
          minHeight: 0,
          minWidth: 0,
          overflow: "hidden",
          position: "relative",
          width: "100%",
          ...(servicesSectionLoading
            ? {
                contain: "inline-size layout style"
              }
            : {})
        }}
      >
        {servicesSectionLoading ? (
          <LinearProgress
            aria-busy
            data-testid="schedule-resource-book-services-linear-progress"
            sx={{
              height: 3,
              left: 0,
              position: "absolute",
              right: 0,
              top: 0,
              zIndex: 2
            }}
            variant="indeterminate"
          />
        ) : null}
        <Accordion
          disabled={!servicesAccordionExpandable}
          elevation={0}
          expanded={servicesExpanded}
          onChange={(_, expanded) => {
            if (!servicesAccordionExpandable) {
              return;
            }
            setServicesExpanded(expanded);
          }}
          sx={[
            scheduleResourceBookAccordionShellNeutralSx,
            { width: "100%" },
            hasMandatoryConferenceService
              ? {
                  "& .MuiAccordionSummary-root": {
                    borderTop: "1px solid",
                    borderBottom: "1px solid",
                    borderRight: "1px solid",
                    borderColor: "warning.main",
                    borderTopRightRadius: "8px",
                    borderBottomRightRadius: "8px"
                  }
                }
              : {}
          ]}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            data-testid="schedule-resource-book-services-collapse"
            sx={scheduleResourceBookAccordionSummaryNeutralSx}
          >
            <Typography fontWeight={600} variant="subtitle1">
              {t("Drawer_Services")}
            </Typography>
          </AccordionSummary>
          <Divider />
          <AccordionDetails
            sx={{
              background: "transparent",
              bgcolor: "transparent",
              pb: { sm: 1.5, xs: 1.25 },
              pt: 1.5,
              px: { sm: 1.5, xs: 1.25 }
            }}
          >
            {!servicesSectionLoading && mappedTabs.length === 0 && (
              <Typography color="text.secondary" variant="body2">
                {t("No services available")}
              </Typography>
            )}

            {!servicesSectionLoading && mappedTabs.length > 0 && (
              <BookingServicePromptDialogContents
                mappedTabs={mappedTabs}
                editedService={editedService}
                setEditedService={setEditedService}
                orderedService={orderedService}
                setOrderedService={setOrderedService}
                selectTab={selectTab}
                setSelectTab={setSelectTab}
              />
            )}
          </AccordionDetails>
        </Accordion>
      </Box>
    </Grid2>
  );
}
